import { useMemo } from 'react';

import { useMeQuery } from '@/store/people/people.store';
import {
  useBilledServiceIdsQuery,
  useSubscriptionQuery,
} from '@/store/subscriptions/subscriptions.store';
import { isVesselUnavailable, useVesselQuery } from '@/store/vessels/vessels.store';

import { canEditPlan, membershipOf } from './subscription.access';
import { isVesselOwner } from './subscription.takeover';
import type { ManagedVesselSubscription } from './useManagedVesselSubscriptions';

/**
 * One vessel and its subscription, both addressed by the vessel's id.
 *
 * The list joins two collections to build the same pair; here each half is asked for
 * directly — `GET /{id}` on the vessel service and `GET /subscriptions` scoped to it — so
 * the detail view costs two single-row reads however it is reached, and works for a vessel
 * that would fall outside the first page of the owner's list.
 *
 * Neither half waits on `me`. The list has to — it is built from the caller's role rows — but
 * the URL already names the service here, so the vessel and its bill render without it. `me`
 * is still read, for the rights that decide whether the plan and seat controls are offered at
 * all; that is a permission on what is drawn, not a gate on fetching it, so a row missing its
 * rights while `me` is in flight reads as view-only for that beat rather than blocking.
 */
export const useVesselSubscription = (vesselId?: string) => {
  const vesselQuery = useVesselQuery(vesselId);
  const subscriptionQuery = useSubscriptionQuery(vesselId);
  const { data: me } = useMeQuery();
  /*
   * Whose card the plan is on. Not a decoration like the rights above: this view carries the
   * controls that write, and the plan a captain pays for is changed by buying it rather than
   * by editing it. Waited for below — offering "Change plan" for a beat on a vessel somebody
   * else pays for would offer to re-price their subscription.
   */
  const billedQuery = useBilledServiceIdsQuery(vesselId);

  /**
   * Both halves must have answered. A missing subscription is `null` — the free plan, and a
   * real answer — but so is a *failed* one if it is taken at face value, which would price
   * a Command vessel at nothing and report that it has no seats. Only a subscription query
   * that actually succeeded is allowed to mean "no row".
   */
  const item = useMemo<ManagedVesselSubscription | null>(
    () =>
      vesselQuery.data && subscriptionQuery.isSuccess && billedQuery.isFetched
        ? {
            vessel: vesselQuery.data,
            subscription: subscriptionQuery.data ?? null,
            canEdit: canEditPlan(membershipOf(me, vesselQuery.data.id)),
            /*
             * A bill that could not be read falls back to the caller's own, which is what
             * every one of these views assumed before a plan could be taken over. Hence
             * `isFetched` rather than `isSuccess`: the answer is waited for, but a failure
             * leaves the view standing rather than blanking it.
             */
            isPayer: billedQuery.data?.has(vesselQuery.data.id) ?? true,
            isOwner: isVesselOwner(vesselQuery.data, me),
          }
        : null,
    [
      me,
      vesselQuery.data,
      subscriptionQuery.isSuccess,
      subscriptionQuery.data,
      billedQuery.isFetched,
      billedQuery.data,
    ],
  );

  const error = vesselQuery.error ?? subscriptionQuery.error;

  return {
    item,
    isLoading: !item && !error,
    /**
     * The vessel is not the caller's, or not there. Distinguished from a plain error
     * because it is the only failure with somewhere to go: the list. A server that broke
     * on a URL that may well be valid is worth saying out loud instead of redirecting
     * away from.
     */
    isUnavailable: isVesselUnavailable(vesselQuery.error),
    error,
  };
};
